import React from "react"
import { Link } from "gatsby"
import Headroom from "react-headroom"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPhoneAlt } from "@fortawesome/free-solid-svg-icons"

import styled from "@emotion/styled"
import theme from "../../utils/theme"

import LogoSVG from "../../images/brand/logo.svg"

const Outer = styled.div`
  @media (max-width: ${theme.breakpoints.md}) {
    display: none;
  }
  height: 100%;
  width: 100%;
  display: grid;
  grid-template-columns:
    minmax(24px, auto) minmax(auto, ${props => props.theme.maxWidth})
    minmax(24px, auto);
  padding: 32px 0;
  background-color: ${props =>
    props.backgroundColor ? props.backgroundColor : "#ffffff"};
`
const Inner = styled.div`
  width: 100%;
  display: grid;
  grid-column: 2;
  grid-template-columns: 320px 1fr;
  column-gap: 32px;
  row-gap: 8px;
  grid-template-areas:
    "logo phone"
    "logo nav";
`
const Logo = styled.img`
  height: 28px;
  margin: 0;
  display: block;
`

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  grid-area: logo;
`
const NavList = styled.div`
  grid-area: nav;
  display: grid;
  gap: 24px;
  grid-auto-flow: column;
  grid-column: auto;
  justify-content: flex-end;
  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: repeat(4, auto);
    grid-template-rows: 1fr 1fr;
  }
  a {
    color: ${props => props.theme.shade[10]};
    text-decoration: none;
  }
`

const NavLink = styled(Link)`
  padding-bottom: 8px;
  &.active {
    position: relative;
    padding-bottom: 8px;
    border-bottom: 2px solid ${props => props.theme.primary};
    @media (max-width: ${theme.breakpoints.lg}) {
      border-bottom: 2px solid ${props => props.theme.primary};
    }
  }
`
const SubNavLink = styled(Link)`
  @media (min-width: ${theme.breakpoints.md}) {
    padding: 8px 16px;
    text-align: left;
    width: 220px;
    background: #ffffffda;
    backdrop-filter: blur(1rem);
    cursor: pointer;
    display: block;
    border-bottom: 1px solid ${props => props.theme.shade[80]};
    transition: 0.4s;
    :hover {
      background: #ffffff;
    }
  }
`
const DropdownContainer = styled.div`
  position: absolute;
  top: 32px;
  border-radius: ${props => props.theme.radius.default};
  overflow: hidden;
  box-shadow: 0 0 2.2px rgba(0, 0, 0, 0.034), 0 6.7px 5.3px rgba(0, 0, 0, 0.048),
    0 12.5px 10px rgba(0, 0, 0, 0.06), 0 22.3px 17.9px rgba(0, 0, 0, 0.072),
    0 41.8px 33.4px rgba(0, 0, 0, 0.086), 0 100px 80px rgba(0, 0, 0, 0.12);
  z-index: 1;
  transition: 0.4s;
  pointer-events: none;
  opacity: 0;
  transform: translate(0, -2px);
`
const PhoneContainer = styled.div`
  grid-area: phone;
  text-align: right;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  color: ${props => props.theme.primary};
  a {
    color: ${props => props.theme.primary};
    text-decoration: none;
  }
  @media (max-width: ${theme.breakpoints.md}) {
    display: none;
  }
`
const Phone = styled(FontAwesomeIcon)`
  margin-right: 8px;
`
const Dropdown = styled.div`
  position: relative;
  ${NavLink} {
    user-select: none;
    pointer-events: none;
  }
  @media (min-width: ${theme.breakpoints.md}) {
    :hover {
      ${DropdownContainer} {
        opacity: 1;
        pointer-events: all;
        transform: translate(0, 0);
      }
    }
  }
  @media (max-width: ${theme.breakpoints.md}) {
    ${NavLink} {
      color: ${props => props.theme.shade[30]};
    }
  }
  @media (max-width: ${theme.breakpoints.xs}) {
    margin: 0;
  }
`

export default function Nav(props) {
  return (
    <Headroom style={{ zIndex: 9 }}>
      <Outer>
        <Inner>
          <PhoneContainer>
            <Phone icon={faPhoneAlt} style={{ width: "18px" }} />
            <a href="tel:+44 115 7870500">+44 115 7870500</a>
          </PhoneContainer>

          <LogoLink to="/">
            <Logo src={LogoSVG} alt="DevelRx Logo" />
          </LogoLink>
          <NavList>
            <NavLink activeClassName="active" to="/">
              Home
            </NavLink>
            <NavLink activeClassName="active" to="/team/">
              Team
            </NavLink>
            <Dropdown>
              <NavLink activeClassName="active" partiallyActive to="/services/">
                Services
              </NavLink>
              <DropdownContainer>
                <SubNavLink to="/services/research-candidate-selection/">
                  Research &amp; Candidate Selection
                </SubNavLink>
                <SubNavLink to="/services/drug-development/">
                  Drug Development
                </SubNavLink>
                <SubNavLink to="/services/registration/">
                  Registration
                </SubNavLink>
                <SubNavLink activeClassName="active" to="/services/marketing/">
                  Marketing
                </SubNavLink>
              </DropdownContainer>
            </Dropdown>
            <NavLink activeClassName="active" partiallyActive to="/news/">
              News &amp; Events
            </NavLink>
            <Dropdown>
              <NavLink
                activeClassName="active"
                partiallyActive
                to="/publications/"
              >
                Publications
              </NavLink>
              <DropdownContainer>
                <SubNavLink
                  activeClassName="active"
                  to="/publications/company/"
                >
                  Company Literature
                </SubNavLink>
                <SubNavLink
                  activeClassName="active"
                  to="/publications/edited-books-and-special-issues/"
                >
                  Edited Books and Special Issues
                </SubNavLink>
                <SubNavLink to="/publications/scientific-articles-drug-abuse-and-dependence/">
                  Scientific Articles
                </SubNavLink>
                <SubNavLink to="/publications/poster-presentations-drug-abuse-and-dependence/">
                  Poster Presentations at Scientific Meetings
                </SubNavLink>
                <SubNavLink to="/publications/communications-at-scientific-meetings/">
                  Communications at Scientific Meetings
                </SubNavLink>
              </DropdownContainer>
            </Dropdown>
            <NavLink activeClassName="active" to="/partners/">
              Partners
            </NavLink>
            <NavLink activeClassName="active" to="/contact/">
              Contact
            </NavLink>
          </NavList>
        </Inner>
      </Outer>
    </Headroom>
  )
}
